<template>
  <div class="katex-content" v-html="renderedContent"></div>
</template>

<script>
import { computed } from 'vue'
import katex from 'katex'
import { marked } from 'marked'
import 'katex/dist/katex.min.css'

export default {
  name: 'KatexContent',
  props: {
    content: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    // Configure marked for safe rendering
    marked.setOptions({
      breaks: true,
      gfm: true
    })

    const renderedContent = computed(() => {
      if (!props.content) return ''
      
      let result = props.content
      
      // Step 1: Protect math expressions by replacing them with placeholders
      const mathBlocks = []
      
      // Display math (block): $$...$$
      result = result.replace(/\$\$([\s\S]+?)\$\$/g, (match, math) => {
        try {
          const rendered = katex.renderToString(math.trim(), {
            displayMode: true,
            throwOnError: false
          })
          mathBlocks.push(rendered)
          return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
        } catch (e) {
          mathBlocks.push(match)
          return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
        }
      })
      
      // Inline math: $...$
      result = result.replace(/\$([^\$\n]+?)\$/g, (match, math) => {
        try {
          const rendered = katex.renderToString(math.trim(), {
            displayMode: false,
            throwOnError: false
          })
          mathBlocks.push(rendered)
          return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
        } catch (e) {
          mathBlocks.push(match)
          return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
        }
      })
      
      // LaTeX block notation: \[...\]
      result = result.replace(/\\\[([\s\S]+?)\\\]/g, (match, math) => {
        try {
          const rendered = katex.renderToString(math.trim(), {
            displayMode: true,
            throwOnError: false
          })
          mathBlocks.push(rendered)
          return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
        } catch (e) {
          mathBlocks.push(match)
          return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
        }
      })
      
      // LaTeX inline notation: \(...\)
      result = result.replace(/\\\(([^\)]+?)\\\)/g, (match, math) => {
        try {
          const rendered = katex.renderToString(math.trim(), {
            displayMode: false,
            throwOnError: false
          })
          mathBlocks.push(rendered)
          return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
        } catch (e) {
          mathBlocks.push(match)
          return `%%MATH_BLOCK_${mathBlocks.length - 1}%%`
        }
      })
      
      // Step 2: Apply markdown formatting
      result = marked.parse(result)
      
      // Step 3: Restore math expressions
      mathBlocks.forEach((block, index) => {
        result = result.replace(`%%MATH_BLOCK_${index}%%`, block)
      })
      
      return result
    })

    return { renderedContent }
  }
}
</script>

<style scoped>
.katex-content {
  line-height: 1.8;
  word-wrap: break-word;
  display: inline;
}

/* KaTeX styles - ensure proper alignment */
.katex-content :deep(.katex) {
  font-size: 1.1em;
  display: inline;
  vertical-align: baseline;
}

.katex-content :deep(.katex-display) {
  margin: 1rem 0;
  overflow-x: auto;
  display: block;
  text-align: center;
}

/* Reset katex-html to prevent alignment issues */
.katex-content :deep(.katex-html) {
  display: inline;
  vertical-align: baseline;
}

.katex-content :deep(.base) {
  display: inline;
  vertical-align: baseline;
}

/* Markdown styles */
.katex-content :deep(h1) {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 1.5rem 0 1rem;
}

.katex-content :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.25rem 0 0.875rem;
}

.katex-content :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1rem 0 0.75rem;
}

.katex-content :deep(p) {
  margin: 0.75rem 0;
}

.katex-content :deep(p:first-child) {
  margin-top: 0;
}

.katex-content :deep(p:last-child) {
  margin-bottom: 0;
}

.katex-content :deep(strong) {
  font-weight: 600;
}

.katex-content :deep(em) {
  font-style: italic;
}

.katex-content :deep(code) {
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.875em;
  color: #dc2626;
}

.katex-content :deep(pre) {
  background: #1f2937;
  color: #f3f4f6;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  margin: 1rem 0;
}

.katex-content :deep(pre code) {
  background: none;
  padding: 0;
  color: inherit;
}

.katex-content :deep(ul),
.katex-content :deep(ol) {
  margin: 0.75rem 0;
  padding-left: 1.5rem;
}

.katex-content :deep(li) {
  margin: 0.375rem 0;
}

.katex-content :deep(blockquote) {
  border-left: 4px solid #6366f1;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #6b7280;
  font-style: italic;
}

.katex-content :deep(a) {
  color: #6366f1;
  text-decoration: none;
}

.katex-content :deep(a:hover) {
  text-decoration: underline;
}

.katex-content :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1.5rem 0;
}

.katex-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.katex-content :deep(th),
.katex-content :deep(td) {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.katex-content :deep(th) {
  background: #f3f4f6;
  font-weight: 600;
}

/* Dark mode */
:global(body.dark-mode) .katex-content :deep(code) {
  background: #374151;
  color: #fca5a5;
}

:global(body.dark-mode) .katex-content :deep(pre) {
  background: #0f172a;
}

:global(body.dark-mode) .katex-content :deep(blockquote) {
  border-left-color: #818cf8;
  color: #9ca3af;
}

:global(body.dark-mode) .katex-content :deep(a) {
  color: #818cf8;
}

:global(body.dark-mode) .katex-content :deep(hr) {
  border-top-color: #374151;
}

:global(body.dark-mode) .katex-content :deep(th),
:global(body.dark-mode) .katex-content :deep(td) {
  border-color: #374151;
}

:global(body.dark-mode) .katex-content :deep(th) {
  background: #1e293b;
}
</style>
